import * as React from 'react';
import { Dropdown, Glyphicon, MenuItem } from 'react-bootstrap';
import Moment from 'react-moment';
import { RouteComponentProps } from 'react-router';
import { of, Subject, timer } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/internal/operators';
import { exhaustMap } from 'rxjs/operators';
import GeolocationService from '../../../api/geolocation_service';
import BusStopService from '../../../api/stadtbus/bus_stop_service';
import PredictionsService from '../../../api/stadtbus/predictions_service';
import { encodeTripId } from '../../../helpers';
import { IBusStop, IPosition } from '../../../interfaces/stadtbus/bus_stop'
import { IPrediction } from '../../../interfaces/stadtbus/predictions'
import { lazyInject } from '../../../inversify';
import { gaEvent } from '../../../with_tracker';
import Theme from '../css/bustheme';
import '../css/bustheme.css';
import { MapImage } from '../line_images';

interface IProps {
  stopID: string | undefined
}

interface IHaltestellenMonitorComponentProps extends Partial<RouteComponentProps<IProps>> {
}

interface IHaltestellenMonitorComponentState {
  busStops: IBusStop[],
  favoriteStopId?: string,
  isRealTime: boolean,
  myLat: number,
  myLng: number,
  loadingPredictions: boolean,
  predictions: IPrediction[],
  selectedBusStop: IBusStop | undefined,
}

class HaltestellenMonitorComponent extends React.Component<IHaltestellenMonitorComponentProps, IHaltestellenMonitorComponentState> {
  @lazyInject(BusStopService)
  private readonly busStopService: BusStopService;
  @lazyInject(PredictionsService)
  private readonly predictionsService: PredictionsService;
  @lazyInject(GeolocationService)
  private readonly geolocationService: GeolocationService;
  @lazyInject(Theme)
  private readonly theme: Theme;

  // emits an event if new predictions were received
  private readonly onLoadPredictions: Subject<IPrediction[]> = new Subject<IPrediction[]>();
  // emits an event if a new bus stop has been selected
  private readonly onSelectBusStop: Subject<IBusStop> = new Subject<IBusStop>();
  // emits an event if the component is unmounted
  private readonly onUnmount: Subject<boolean> = new Subject<boolean>();

  constructor(props: IHaltestellenMonitorComponentProps) {
    super(props);

    this.state = {
      busStops: [],
      favoriteStopId: undefined,
      isRealTime: false,
      loadingPredictions: true,
      myLat: -1,
      myLng: -1,
      predictions: [],
      selectedBusStop: undefined,
    };

    this.selectBusStop = this.selectBusStop.bind(this);
  }

  public componentDidMount() {
    // Receive the selected bus stop and update the state
    this.onSelectBusStop
      .pipe(takeUntil(this.onUnmount))
      .subscribe(s => {
        // Set the selectedBusStop
        this.setState({
          selectedBusStop: s,
        });
      });

    // Receive the selected bus stop and clear the predictions if the new ones were not loaded in 150ms
    this.onSelectBusStop
      .pipe(
        // Create a timer that is canceled by onLoadPredictions
        switchMap(() => timer(150)
          .pipe(takeUntil(this.onLoadPredictions))),
        // Cancel the complete subscription onUnmount
        takeUntil(this.onUnmount),
      )
      .subscribe(() => {
        this.setState({
          isRealTime: false,
          loadingPredictions: true,
          predictions: [],
        })
      });

    // Receive the selected bus stop and load the corresponding predictions
    this.onSelectBusStop
      .pipe(
        switchMap(s => this.predictionsService.onStopPredictions(s.id, false)),
        takeUntil(this.onUnmount),
      )
      .subscribe(predictions => {
        // cancel the timer
        this.onLoadPredictions.next(predictions);
      });

    // Receive the loaded predictions and update the state
    this.onLoadPredictions
      .pipe(takeUntil(this.onUnmount))
      .subscribe(predictions => {
        this.setState({
          isRealTime: predictions.find(p => p.isRealTime) !== undefined,
          loadingPredictions: false,
          predictions,
        });
      });

    // load the contents.
    this.componentWillReceiveProps(this.props);

    // Receive the bus stops and update the state
    this.busStopService.onBusStops
      .pipe(takeUntil(this.onUnmount))
      .subscribe(stops => {
        this.setState({
          busStops: stops.sort((a: IBusStop, b: IBusStop) => a.name.localeCompare(b.name)),
        });
      });

    this.busStopService.onFavoriteBusStop
      .pipe(takeUntil(this.onUnmount))
      .subscribe(fav => {
        this.setState({
          favoriteStopId: fav ? fav.id : fav,
        });
      });

    // Subscribe to the geolocation. Retry each 5 seconds if the service crashed
    timer(0, 5000)
      .pipe(
        exhaustMap(() => {
          return this.geolocationService.watchPosition()
        }),
        takeUntil(this.onUnmount),
      ).subscribe((position: IPosition) => {
      this.setState({
        myLat: position.latitude,
        myLng: position.longitude
      });
    });
  }

  public componentWillReceiveProps(props: IHaltestellenMonitorComponentProps) {
    const selectedStopId = props.match!.params.stopID;

    // Get the bus stop or undefined if not exists
    this.busStopService.onBusStopExists(selectedStopId)
      .pipe(
        // return the existing stop or request the favorite bus stop
        switchMap(existingStop => existingStop ? of(existingStop) : this.busStopService.onFavoriteBusStop),
        // onFavoriteBusStop can be undefined if no bus stops have been received from the server
        filter(t => t !== undefined),
        // we only need one single entry => unsubscribe
        take(1),
      ).subscribe((t: IBusStop) => {
        if (t.id !== selectedStopId) {
          // we received a different stop than requested so either selectedStopId doesn't exist or was undefined. We
          // REPLACE the most recent history entry instead of pushing a new state so we don't break the browser history
          props.history!.replace(`${process.env.PUBLIC_URL}/stadtbus/haltestellen/${t.id}`);
        } else {
          this.onSelectBusStop.next(t!);
        }
      }
    );
  }

  public componentWillUnmount() {
    // Notify that the component will get unmounted
    this.onUnmount.next(true);
  }

  public render() {
    return (
      <div className="container">
        <div className="row">
          <div className="col-xs-12 headlineText">Haltestelle:</div>
          <div className="col-xs-12">
            <table>
              <tbody>
              <tr>
                <td style={{paddingRight: "8px", width: "100%"}}>
                  <select className="form-control"
                          value={this.state.selectedBusStop ? this.state.selectedBusStop.id : ''}
                          onChange={this.selectBusStop}
                          title="Haltestelle wählen">
                    {this.state.busStops.map((stop: IBusStop) => (
                      <option key={stop.id} value={stop.id}>{stop.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{paddingRight: "8px"}}
                    className={(!this.geolocationService.geolocationAvailable() ? "displayNone" : "")}>
                  <button className="btn btn-default" onClick={e => this.setSelectedBusStopToNearst()}
                          title="Nächste Haltestelle anzeigen">
                    <span className="glyphicon glyphicon-map-marker"/>
                  </button>
                </td>
                <td>
                  <Dropdown id="dropdown-custom-1">
                    <Dropdown.Toggle>
                      <Glyphicon glyph="star" style={{color: (this.isFavoriteBusStop()) ? "#efce4a" : "black"}}/>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="dropdown-menu-right">
                      <MenuItem style={{padding: "4px"}} onClick={() => this.selectFavoriteBusStop()}>Favorit
                        anzeigen</MenuItem>
                      <MenuItem style={{padding: "4px"}}
                                onClick={() => this.setFavorite()}>{this.state.selectedBusStop ? this.state.selectedBusStop.name : ''} als
                        Favorit setzen</MenuItem>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="row" style={{marginTop: "16px"}}>
          <div className="panel panel-default">
            <div className="panel-heading">
              <table>
                <tbody>
                <tr>
                  <td>
                    <div className="circle">H</div>
                  </td>
                  <td style={{padding: "16px", width: "100%"}} className="haltestellenText">
                    {this.state.selectedBusStop ? this.state.selectedBusStop.name : ''}
                  </td>
                  <td style={{padding: "8px"}} title="Distanz zur Haltestelle" className="haltestellenText">
                    {this.showDistance()}
                  </td>
                  <td style={{padding: "8px"}} className="haltestellenText">
                    {this.state.selectedBusStop && this.state.selectedBusStop.position && (
                      <button className="btn btn-default" onClick={() => this.showBusStopInMap()}
                              title="In Karte anzeigen">
                        <MapImage style={{display: 'block', height: '16px', margin: '2 0', width: '16px'}}/>
                      </button>
                    )}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <div className="panel-body" style={{paddingTop: "0px"}}>
              {
                this.state.predictions.length > 0 ? (
                  <table>
                    <thead>
                    <tr>
                      <th style={{textAlign: "center"}} className="haltestellenText">
                        Linie
                      </th>
                      <th style={{padding: "16px", fontSize: "18px"}} className="haltestellenText">
                        Richtung
                      </th>
                      <th style={{padding: "8px", fontSize: "18px"}} className="haltestellenText">
                        Abfahrt (+Versp.)
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    {this.state.predictions.reduce((arr, curr, idx, predictions) => {
                      const currDay = new Date().getDate();

                      // Only display entries of a future day if less than 12 entries are displayed yet
                      if (curr.shouldTime.getDate() === currDay || arr.length < 12) {
                        const prevTime = idx > 0 ? predictions[idx - 1].shouldTime.getDate() : currDay;

                        // Add separator if we show predictions for future days.
                        if (prevTime !== curr.shouldTime.getDate()) {
                          // add AST info if no entries of the current day left
                          if (arr.length === 0) {
                            arr.push(this.renderAST()); 
                          }
                          arr.push(<tr key={curr.shouldTime.toLocaleDateString()}>
                              <td colSpan={3}>
                                <hr className="hr-text"
                                    data-content={"Fahrten am " + curr.shouldTime.toLocaleDateString()}/>
                              </td>
                            </tr>
                          );
                        }
  
                        arr.push(this.renderLine(curr, (curr.shouldTime.getDate() !== new Date().getDate())));
                      }

                      return arr;
                    }, [] as React.ReactNodeArray)}
                    </tbody>
                  </table>
                ) : (
                  <div style={{width: "100%", textAlign: "center", marginTop: "16px"}}>
                    {this.state.loadingPredictions ? 'Informationen werden geladen.' : 'Derzeit sind leider keine Informationen verfügbar.'}
                  </div>
                )
              }
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderLine(prediction: IPrediction, isFuture: boolean) {
    return (
      <tr key={`${encodeTripId(prediction.journeyRef, prediction.operationDate)}`} className="clickable"
          onClick={() => this.showTrip(prediction)}
          title={"Zeige Fahrplan für Linie " + prediction.lineName}
          style={{color: (isFuture) ? "gray" : "black"}}>
        <td>
          <div className={"circleSmall " + this.theme.getLineColor(prediction.lineName)}>{prediction.lineName}</div>
        </td>
        <td style={{padding: "16px", width: "100%"}} className="haltestellenText">
          {prediction.lineDestination}
        </td>
        <td style={{padding: "8px"}} className="haltestellenText">
          <Moment format='HH:mm' date={prediction.shouldTime}/>
          {(prediction.isRealTime) ?
            <div style={{marginLeft: "8px", color: prediction.delay > 0 ? "red" : "green", display: "inline"}}>
              +{prediction.delay}
            </div>
            : <div/>
          }
        </td>
      </tr>
    );
  }

  private renderAST() {
    const arr = [];
    arr.push(<tr>
      <td colSpan={3}>
        <hr className="hr-text"
            data-content= {"Derzeit kein Linienbetrieb"}/>
      </td>
    </tr>
    );
    arr.push(<tr>
      <td style={{fontSize: "18px", fontWeight: "bold"}} colSpan={3} className="haltestellenText">
        Bleiben Sie mobil mit dem Anruf-Sammel-Taxi!
      </td>
      <td />
    </tr>
    );
    arr.push(<tr>
        <td style={{fontSize: "16px", width: "100%"}} colSpan={3} className="haltestellenText">
          <p>
            Anmeldung 60 Minuten vor der gewünschten Abfahrt unter 05261/6673950.
          </p>
          <p>
            <table>
              <tr style={{verticalAlign: "top"}}>
                <td>Montag bis Freitag:</td>
                <td style={{paddingLeft: "10px"}}>19:45–00:15 Uhr</td>
              </tr>
              <tr style={{verticalAlign: "top"}}>
                <td>Samstag:</td>
                <td style={{paddingLeft: "10px"}}>16:45–00:15 Uhr</td>
              </tr>
              <tr style={{verticalAlign: "top"}}>
                <td>Sonn- und Feiertage:</td>
                <td style={{paddingLeft: "10px"}}>08:45–22:15 Uhr</td>
              </tr>
            </table>
          </p>
        </td>
        <td />
      </tr>
      );
      return arr;
  }

  private selectBusStop(e: any) {
    this.props.history!.push(`${process.env.PUBLIC_URL}/stadtbus/haltestellen/${e.target.value}`);
  }

  private selectFavoriteBusStop() {
    if (this.state.favoriteStopId) {
      this.props.history!.push(`${process.env.PUBLIC_URL}/stadtbus/haltestellen/${this.state.favoriteStopId}`);
    }
  }

  private setFavorite() {
    if (this.state.selectedBusStop) {
      this.busStopService.updateFavoriteBusStopId(this.state.selectedBusStop.id);

      gaEvent('fahrgast_component', 'select_favorite_bus_stop', +this.state.selectedBusStop.id);
    }
  }

  private isFavoriteBusStop(): boolean {
    if (this.state.selectedBusStop) {
      return this.state.selectedBusStop.id === this.state.favoriteStopId;
    }
    return false;
  }

  private showTrip(trip: IPrediction): void {
    if (this.props.history) {
      const tripId = encodeTripId(trip.journeyRef, trip.operationDate);
      const stopIdParameter = this.state.selectedBusStop ? `/${this.state.selectedBusStop.id}` : '';
      this.props.history.push(`${process.env.PUBLIC_URL}/stadtbus/linie/${tripId}${stopIdParameter}`);
    }
  }

  private showDistance(): string {
    if (this.state.selectedBusStop && this.state.selectedBusStop.position && this.state.myLat !== -1 && this.state.myLng !== -1) {
      const busStopLng: number = this.state.selectedBusStop.position.longitude;
      const busStopLat: number = this.state.selectedBusStop.position.latitude;
      const myLng: number = this.state.myLng;
      const myLat: number = this.state.myLat;
      const distance = Math.floor(this.geolocationService.distanceInKmBetweenEarthCoordinates(busStopLat, busStopLng, myLat, myLng) * 1000);
      if (distance < 10000) {
        return "~" + this.geolocationService.formatDistance(distance);
      }
    }
    return "";
  }

  private setSelectedBusStopToNearst(): void {
    gaEvent('fahrgast_component', 'select_nearest_bus_stop');

    if (this.state.busStops && this.state.busStops.length > 0 && this.state.myLat !== -1 && this.state.myLng !== -1) {
      const myLng: number = this.state.myLng;
      const myLat: number = this.state.myLat;

      const distances: number[] = this.state.busStops.map(stop => {
        if (stop.position) {
          return this.geolocationService.distanceInKmBetweenEarthCoordinates(stop.position.latitude, stop.position.longitude, myLat, myLng)
        } else {
          return Number.MAX_VALUE;
        }
      });

      // Find smallest index
      const nearstIndex = distances.reduce((iMin, x, i, arr) => x < arr[iMin] ? i : iMin, 0);
      const nearst: IBusStop = this.state.busStops[nearstIndex];

      if (nearst) {
        this.props.history!.push(`${process.env.PUBLIC_URL}/stadtbus/haltestellen/${nearst.id}`);
      }
    }
  }

  private showBusStopInMap(): void {
    gaEvent('fahrgast_component', 'show_bus_stop_in_map');

    if (this.props.history && this.state.selectedBusStop && this.state.selectedBusStop.position) {
      const lat = this.state.selectedBusStop.position.latitude;
      const lng = this.state.selectedBusStop.position.longitude;
      this.props.history.push('/stadtbus/karte/' + lat + '/' + lng);
    }
  }
}

export default HaltestellenMonitorComponent;



// WEBPACK FOOTER //
// ./src/components/stadtbus/fahrgast_components/haltestellen_monitor_component.tsx